import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useCloudSave } from '../hooks/useCloudSave';
import { exportTreeToJSON } from './treeExporter';
import { trackEvent } from './analytics';
import { checkQuotaLimitLocal } from './quotaCache';

/**
 * Utility function to save a newly named canvas to the cloud
 */
export const saveNewlyNamedCanvas = async (
  canvasName: string,
  user: any,
  showToast: (message: string, type: 'success' | 'error' | 'info') => void,
  showAlert: (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success') => void
): Promise<{ success: boolean; canvasId?: string; error?: string }> => {
  try {
    // Check canvas creation quota using cache (consistent with other quota checks)
    try {
      const hasExceeded = await checkQuotaLimitLocal(user.uid, 'totalCanvases');
      if (hasExceeded) {
        showAlert(
          'Canvas Creation Limit Reached',
          'You\'ve reached your canvas limit. Please upgrade your plan to create more canvases.',
          'danger'
        );
        return { 
          success: false, 
          error: 'Canvas creation limit reached'
        };
      }
    } catch (quotaError) {
      console.error('Error checking canvas creation quota:', quotaError);
      // Continue with canvas creation if quota check fails
    }

    // Get current whiteboard state
    const state = useWhiteboardStore.getState().state;
    
    // Export current state to JSON
    const jsonContent = exportTreeToJSON(state);
    
    // Use cloud save hook functionality directly through service
    const { CloudSaveService } = await import('../services/cloudSave/cloudSaveService');
    
    const result = await CloudSaveService.saveCanvas(
      jsonContent,
      canvasName,
      user.uid
    );

    if (result.success && result.canvasId) {
      // Canvas creation quota is now handled by CloudSaveService when creating new canvases
      // No need to increment here as it's already done in the service

      // Update the store with the new canvas name and ID
      const { setCanvasName, setCanvasId, setSaveStatus } = useWhiteboardStore.getState();
      setCanvasName(canvasName);
      setCanvasId(result.canvasId);
      
      // Set save status to indicate the canvas has been saved
      const savedStateHash = exportTreeToJSON(state, { forComparison: true });
      setSaveStatus(savedStateHash);

      // Track the canvas creation event
      trackEvent('canvas_created_and_named', {
        canvas_id: result.canvasId,
        canvas_name: canvasName,
        notes_count: state.notes.length
      });

      showToast(`Canvas "${canvasName}" saved successfully`, 'success');
      
      return { 
        success: true, 
        canvasId: result.canvasId 
      };
    } else {
      showToast(result.error || 'Failed to save canvas', 'error');
      return { 
        success: false, 
        error: result.error || 'Failed to save canvas' 
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('Error saving newly named canvas:', error);
    showToast(`Error saving canvas: ${errorMessage}`, 'error');
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

/**
 * Check if the current canvas needs to be named (i.e., it's a new canvas)
 * Also hide welcome message if there are notes on the canvas
 */
export const needsCanvasNaming = (canvasId?: string): boolean => {
  // If there's a canvasId, no need for naming
  if (canvasId) {
    return false;
  }
  
  // If no canvasId, check if there are notes
  // If there are notes, hide the welcome message (canvas is being used)
  const state = useWhiteboardStore.getState().state;
  return state.notes.length === 0;
};

/**
 * Handle the complete canvas naming flow
 */
export const handleCanvasNaming = async (
  canvasName: string,
  user: any,
  showToast: (message: string, type: 'success' | 'error' | 'info') => void,
  showAlert: (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success') => void,
  onSuccess?: (canvasId: string) => void,
  onError?: (error: string) => void
): Promise<void> => {
  const result = await saveNewlyNamedCanvas(canvasName, user, showToast, showAlert);
  
  if (result.success && result.canvasId) {
    onSuccess?.(result.canvasId);
  } else {
    onError?.(result.error || 'Failed to save canvas');
  }
};
